// src/router/guards/geolocationGuard.js

import { Dialog, LocalStorage } from 'quasar'
import { Capacitor } from '@capacitor/core'
import { START_LOCATION } from 'vue-router'
import { useGeolocationStore } from 'src/stores'
import { log } from 'src/utils/logTracker'
import { i18n } from '@/boot/i18n'
import { mockContextFromRoute } from '@/geolocation/mock-location-log'
import { isMockSimulatorActive } from '@/geolocation/mock-simulator'

const MAX_POSITION_AGE_MINUTES = 2.5
const HOME_ROUTE = 'navigation.home'

// 🔹 Manejador de errores de geolocalización
// Capacitor nativo (Android/iOS) usa códigos string "OS-PLUG-GLOC-00XX" —
// no los códigos numéricos 1/2/3 del PositionError del navegador (esos solo
// aplican al fallback web de @capacitor/geolocation, que esta guarda nunca usa
// porque solo corre cuando Capacitor.isNativePlatform() es true).
// Los códigos "BINGEO-00XX" los emite el plugin propio BinnacleGeo.
function geolocationError(error) {
  const code = error?.code

  // BINGEO-04 (ubicación ficticia con política block) ya mostró su aviso en el store.
  if (code === 'BINGEO-04') return

  let message = 'Error inesperado al intentar conseguir la geolocalización.'

  switch (code) {
    case 'OS-PLUG-GLOC-0003': // permissionDenied
    case 'OS-PLUG-GLOC-0008': // permissionRestricted (iOS)
      message = 'El dispositivo no ha permitido obtener la ubicación. Revise los permisos.'
      break
    case 'OS-PLUG-GLOC-0002': // positionUnavailable
    case 'OS-PLUG-GLOC-0007': // locationServicesDisabled
      message = 'El dispositivo no pudo obtener la ubicación actual.'
      break
    case 'OS-PLUG-GLOC-0010': // GET_LOCATION_TIMEOUT (Android)
      message = 'El GPS está tardando demasiado en responder.'
      break
    case 'BINGEO-01': // permiso no otorgado
      message = i18n.global.t('geolocation.permission_denied')
      break
    case 'BINGEO-02': // servicios de ubicación apagados / posición no disponible
      message = i18n.global.t('geolocation.position_unavailable')
      break
    case 'BINGEO-03': // timeout
      message = i18n.global.t('geolocation.timeout')
      break
    case 'BINGEO-05': // solo ubicación aproximada (Android 12+) / precisión reducida (iOS 14+)
      message = i18n.global.t('geolocation.precise_location_required')
      break
  }

  Dialog.create({
    title: 'Error',
    message
  })
}

function isStalePosition(position) {
  if (!position.timestamp) return false

  const diffMinutes = ((new Date() - new Date(position.timestamp)) / 1000) / 60

  return diffMinutes > MAX_POSITION_AGE_MINUTES
}

// La posición cacheada no vuelve a pasar por fetchCurrent(), así que su validación de ubicación
// ficticia se aplica aquí, con el mismo getter y el mismo aviso del store.
function blockedByMockedPosition(geolocationStore, mockContext) {
  if (!geolocationStore.shouldBlockForMock) return false

  geolocationStore.notifyMockBlocked(mockContext)

  return true
}

function routeLabel(route) {
  if (!route) return '?'

  return route.name || route.fullPath || route.path || '?'
}

function positionAgeSeconds(position) {
  if (!position || !position.timestamp) return 'n/a'

  return Math.round((Date.now() - new Date(position.timestamp).getTime()) / 1000)
}

// Cancelar la navegacion deja al usuario donde estaba. En la primera navegacion de la app (link
// directo, abrir una notificacion, reanudar en esa ruta) no hay pantalla anterior y quedaba en
// blanco detras del aviso: ahi se manda a Inicio para que pueda seguir navegando.
function stayOrGoHome(from, next) {
  if (from === START_LOCATION) {
    next({ name: HOME_ROUTE, replace: true })
    return
  }

  next(false)
}

// 🔹 Navigation Guard con todo integrado
export async function geolocationGuard(to, from, next, origin = 'route') {
  const geolocationStore = useGeolocationStore()

  // TKT-2149 · instrumentación temporal: visible en el LogTracker (7 taps en la esquina inferior
  // izquierda). En release los console.* se borran por drop_console, este buffer no.
  const trace = (outcome, detail = {}) => {
    const extra = Object.entries(detail).map(([key, value]) => `${key}=${value}`).join(' ')

    log.info(
      `[geo-guard] origin=${origin} to=${routeLabel(to)} from=${routeLabel(from)}` +
      ` policy=${geolocationStore.mockPolicy} outcome=${outcome}${extra ? ' ' + extra : ''}`
    )
  }

  // Recuperación de LMK: el usuario ya pasó el guard antes de abrir la cámara,
  // así que no bloqueamos esta navegación. Consumir la bandera de inmediato.
  if (LocalStorage.getItem('lmkRecovery')) {
    LocalStorage.remove('lmkRecovery')
    trace('skip-lmk')
    next()
    return
  }

  try {
    // En web no pedimos ubicación, salvo con el simulador de dev (mock-simulator.js)
    const simulating = isMockSimulatorActive()
    if (!Capacitor.isNativePlatform() && !simulating) {
      trace('skip-web')
      next()
      return
    }

    const current = geolocationStore.current
    const mockContext = mockContextFromRoute(to)

    // Con el simulador se pide siempre una posicion nueva: la cacheada trae el modo anterior.
    if (!current || isStalePosition(current) || simulating) {
      try {
        await geolocationStore.fetchCurrent({ mockContext })
        trace('fetched', { isMock: geolocationStore.current?.isMock })
      } catch (err) {
        trace('error', { code: err?.code })
        geolocationError(err)
        stayOrGoHome(from, next)
        return
      }
    } else {
      trace('reused-cache', { ageSeconds: positionAgeSeconds(current), isMock: current.isMock })
    }

    if (blockedByMockedPosition(geolocationStore, mockContext)) {
      trace('blocked-mock')
      stayOrGoHome(from, next)
      return
    }

    trace('pass')
    next()
  } catch (err) {
    // Cualquier error inesperado entra aquí
    trace('error-unexpected', { code: err?.code })
    geolocationError(err)
    stayOrGoHome(from, next)
  }
}
